<p-toast />

<div class="card">
    <div class="font-semibold text-xl mb-4">Site Templates</div>

    @if (service.error()) {
        <p-message severity="error" [text]="service.error()!" styleClass="mb-3" />
    }

    <p-table
        [value]="service.templates()"
        [loading]="service.loading()"
        dataKey="SiteTemplateId"
        [rowTrackBy]="trackById"
        [rows]="10"
        [paginator]="true"
        [showGridlines]="true"
        responsiveLayout="scroll"
    >
        <ng-template #caption>
            <div class="flex items-center justify-between gap-3 flex-wrap">
                <div class="font-medium">Immutable S3-backed site builds, served by Host</div>
                <div class="flex items-center gap-2">
                    <p-button
                        label="Refresh"
                        icon="pi pi-refresh"
                        severity="secondary"
                        outlined
                        [loading]="service.loading()"
                        (onClick)="refresh()"
                    />
                    <p-button label="Add Template" icon="pi pi-plus" (onClick)="openCreate()" />
                </div>
            </div>
        </ng-template>
        <ng-template #header>
            <tr>
                <th>Name</th>
                <th style="width: 12rem">Type</th>
                <th style="width: 11rem">Current version</th>
                <th>Source</th>
                <th style="width: 11rem">Actions</th>
            </tr>
        </ng-template>
        <ng-template #body let-template>
            <tr>
                <td>
                    <span class="font-medium">{{ template.Name }}</span>
                    <div class="text-sm font-mono text-muted-color break-all">{{ template.SiteTemplateId }}</div>
                </td>
                <td>{{ typeLabelOf(template.CategoryType) }}</td>
                <td>
                    @if (template.CurrentVersion) {
                        <p-tag severity="success" [value]="'v' + template.CurrentVersion" />
                    } @else {
                        <p-tag severity="warn" value="not published" />
                    }
                </td>
                <td>
                    <span class="text-sm font-mono text-muted-color break-all">
                        {{ template.Bucket }}/{{ template.BasePrefix }}
                    </span>
                </td>
                <td>
                    <div class="flex items-center gap-2">
                        <p-button
                            icon="pi pi-info-circle"
                            severity="secondary"
                            [rounded]="true"
                            [outlined]="true"
                            pTooltip="Template info"
                            (onClick)="openInfo(template)"
                        />
                        <p-button
                            icon="pi pi-pencil"
                            severity="warn"
                            [rounded]="true"
                            [outlined]="true"
                            pTooltip="Rename"
                            (onClick)="openRename(template)"
                        />
                        <p-button
                            icon="pi pi-upload"
                            severity="success"
                            [rounded]="true"
                            [outlined]="true"
                            pTooltip="Source & publish"
                            (onClick)="openSource(template)"
                        />
                    </div>
                </td>
            </tr>
        </ng-template>
        <ng-template #emptymessage>
            <tr>
                <td colspan="5" class="text-center py-8 text-muted-color">No site templates yet.</td>
            </tr>
        </ng-template>
    </p-table>
</div>

<!-- ── Create / rename dialog ─────────────────────────────────────────────── -->
<p-dialog
    [header]="dialogTitle()"
    [visible]="dialogVisible()"
    (visibleChange)="dialogVisible.set($event)"
    [style]="{ width: '42rem' }"
    [modal]="true"
    [closable]="!saving()"
>
    <p-fluid>
        <div class="flex flex-col gap-4">
            @if (mode() === 'add') {
                <div class="flex flex-col gap-1">
                    <label for="templateId" class="font-semibold">ID</label>
                    <input
                        pInputText
                        id="templateId"
                        [ngModel]="id()"
                        (ngModelChange)="id.set($event)"
                        placeholder="skill-template-1"
                        [disabled]="saving()"
                    />
                    @if (idError()) {
                        <small class="text-red-500">{{ idError() }}</small>
                    }
                </div>
            }

            <div class="flex flex-col gap-1">
                <label for="templateName" class="font-semibold">Name</label>
                <input
                    pInputText
                    id="templateName"
                    [ngModel]="name()"
                    (ngModelChange)="name.set($event)"
                    placeholder="Skill Template 1"
                    [disabled]="saving()"
                />
                @if (nameError()) {
                    <small class="text-red-500">{{ nameError() }}</small>
                }
            </div>

            <div class="flex flex-col gap-1">
                <label for="templateType" class="font-semibold">Category type</label>
                @if (mode() === 'add') {
                    <p-select
                        inputId="templateType"
                        [options]="typeOptions"
                        optionLabel="label"
                        optionValue="value"
                        [ngModel]="type()"
                        (ngModelChange)="type.set($event)"
                        appendTo="body"
                        [disabled]="saving()"
                    />
                } @else {
                    <input pInputText id="templateType" [ngModel]="typeLabelOf(type())" [disabled]="true" readonly />
                    <small class="text-muted-color">A template only serves realms of its own type — set on creation.</small>
                }
            </div>

            @if (mode() === 'add') {
                <div class="flex flex-col gap-1">
                    <label for="templateBucket" class="font-semibold">Bucket</label>
                    <input pInputText id="templateBucket" [ngModel]="defaultBucket" [disabled]="true" readonly />
                    @if (defaultBucket) {
                        <small class="text-muted-color">Fixed per environment.</small>
                    } @else {
                        <small class="text-red-500">No site-templates bucket configured for this environment.</small>
                    }
                </div>

                <div class="flex flex-col gap-1">
                    <label for="templatePrefix" class="font-semibold">Base prefix</label>
                    <input pInputText id="templatePrefix" [ngModel]="derivedBasePrefix()" [disabled]="true" readonly placeholder="site-templates/<id>" />
                    <small class="text-muted-color">Derived from the ID. Files resolve at {{ '{Bucket}/{BasePrefix}/{Version}/...' }}.</small>
                </div>
            } @else {
                <div class="flex flex-col gap-1">
                    <span class="font-semibold">Source</span>
                    <span class="text-sm font-mono text-muted-color break-all">{{ bucket() }}/{{ basePrefix() }}</span>
                    <small class="text-muted-color">Bucket and prefix are changed when publishing a version.</small>
                </div>
            }

            @if (generalError()) {
                <small class="text-red-500">{{ generalError() }}</small>
            }
        </div>
    </p-fluid>
    <ng-template #footer>
        <div class="flex justify-end gap-2">
            <p-button label="Cancel" severity="secondary" [disabled]="saving()" (onClick)="close()" />
            <p-button label="Save" icon="pi pi-check" [loading]="saving()" (onClick)="save()" />
        </div>
    </ng-template>
</p-dialog>

<!-- ── Source & publish dialog ────────────────────────────────────────────── -->
<p-dialog
    [header]="sourceTitle()"
    [visible]="sourceDialogVisible()"
    (visibleChange)="sourceDialogVisible.set($event)"
    [style]="{ width: '46rem' }"
    [modal]="true"
    [closable]="!publishing()"
>
    @if (sourceTarget(); as target) {
        <p-fluid>
            <div class="flex flex-col gap-4">
                <p-message
                    severity="warn"
                    text="Publishing flips the live pointer immediately — a realm pointed at this template serves the new version at once. Rollback is the same flow with an older version."
                    styleClass="text-sm"
                />

                <div class="flex flex-col gap-1">
                    <span class="font-semibold">Stored source</span>
                    <span class="text-sm font-mono text-muted-color break-all">{{ target.Bucket }}/{{ target.BasePrefix }}</span>
                    <span class="text-sm">
                        Current version:
                        @if (target.CurrentVersion) {
                            <span class="font-mono">v{{ target.CurrentVersion }}</span>
                        } @else {
                            <span class="text-muted-color">none (offline)</span>
                        }
                    </span>
                </div>

                <div class="flex flex-col gap-1">
                    <div class="flex items-center justify-between">
                        <label for="versionSelect" class="font-semibold">Version</label>
                        <p-button
                            label="Reload"
                            icon="pi pi-refresh"
                            size="small"
                            severity="secondary"
                            text
                            [loading]="versionsLoading()"
                            (onClick)="reloadVersions()"
                        />
                    </div>
                    @if (versionOptions().length > 0) {
                        <p-select
                            inputId="versionSelect"
                            [options]="versionOptions()"
                            optionLabel="label"
                            optionValue="value"
                            [ngModel]="selectedVersion()"
                            (ngModelChange)="selectedVersion.set($event)"
                            placeholder="Select a version"
                            appendTo="body"
                            [disabled]="publishing() || versionsLoading()"
                        />
                    } @else {
                        <input
                            pInputText
                            id="versionSelect"
                            [ngModel]="selectedVersion()"
                            (ngModelChange)="selectedVersion.set($event)"
                            placeholder="No versions discovered — type one to validate"
                            [disabled]="publishing()"
                        />
                        <small class="text-muted-color">No version directories found in storage yet — CI may not have uploaded a build.</small>
                    }
                </div>

                @if (validateResult(); as vr) {
                    @switch (vr.kind) {
                        @case ('manifest') {
                            <div class="flex flex-col gap-2 pt-3 border-t border-surface-200 dark:border-surface-700">
                                <div class="font-semibold">Cache rules from <span class="font-mono">site-manifest.json</span></div>
                                @if (vr.errors.length > 0) {
                                    <p-message severity="error" styleClass="text-sm">
                                        <div class="flex flex-col gap-1">
                                            <span class="font-medium">Manifest errors:</span>
                                            @for (err of vr.errors; track err) {
                                                <span class="font-mono text-xs">{{ err }}</span>
                                            }
                                        </div>
                                    </p-message>
                                }
                                <p-table [value]="vr.rules" [showGridlines]="true" responsiveLayout="scroll">
                                    <ng-template #header>
                                        <tr>
                                            <th>Pattern</th>
                                            <th style="width: 10rem">Disposition</th>
                                        </tr>
                                    </ng-template>
                                    <ng-template #body let-rule>
                                        <tr>
                                            <td><span class="font-mono text-sm break-all">{{ rule.Pattern }}</span></td>
                                            <td>
                                                <p-tag
                                                    [severity]="dispositionSeverity(rule.Disposition)"
                                                    [value]="dispositionLabel(rule.Disposition)"
                                                />
                                            </td>
                                        </tr>
                                    </ng-template>
                                    <ng-template #emptymessage>
                                        <tr>
                                            <td colspan="2" class="text-center py-4 text-muted-color">No explicit rules — convention applies.</td>
                                        </tr>
                                    </ng-template>
                                </p-table>
                            </div>
                        }
                        @case ('no-manifest') {
                            <p-message severity="success" styleClass="text-sm"
                                text="Version resolves in storage. No site-manifest.json — convention caching applies (assets/** immutable, *.html html, rest revalidate)." />
                        }
                        @case ('error') {
                            @if (vr.reason === 'VersionNotInStorage') {
                                <p-message severity="error" styleClass="text-sm"
                                    text="This version directory is missing or empty in storage. Check the version, bucket and prefix — CI may not have finished." />
                            } @else if (vr.reason === 'NotFound') {
                                <p-message severity="error" styleClass="text-sm" text="Template no longer exists." />
                            } @else {
                                <p-message severity="error" styleClass="text-sm" text="Validation failed." />
                            }
                        }
                    }
                }

                @if (sourceError()) {
                    <small class="text-red-500">{{ sourceError() }}</small>
                }
            </div>
        </p-fluid>
        <ng-template #footer>
            <div class="flex justify-between gap-2 w-full">
                <p-button
                    label="Validate"
                    icon="pi pi-search"
                    severity="secondary"
                    outlined
                    [loading]="validating()"
                    [disabled]="publishing()"
                    (onClick)="validate()"
                />
                <div class="flex gap-2">
                    <p-button label="Close" severity="secondary" [disabled]="publishing()" (onClick)="closeSource()" />
                    <p-button label="Publish" icon="pi pi-upload" [loading]="publishing()" (onClick)="publish()" />
                </div>
            </div>
        </ng-template>
    }
</p-dialog>

<!-- ── Template Info dialog ────────────────────────────────────────────────── -->
<p-dialog
    [header]="infoTarget() ? 'Template Info — ' + infoTarget()!.Name : 'Template Info'"
    [visible]="infoDialogVisible()"
    (visibleChange)="infoDialogVisible.set($event)"
    [style]="{ width: '46rem' }"
    [breakpoints]="{ '1024px': '95vw' }"
    [modal]="true"
    [dismissableMask]="true"
>
    <app-template-info
        [brandKit]="infoBrandKit()"
        [loading]="infoLoading()"
        [emptyMessage]="infoEmpty()"
    />
    <ng-template #footer>
        <p-button label="Close" severity="secondary" (onClick)="closeInfo()" />
    </ng-template>
</p-dialog>
